import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { Printer, FileText } from "react-feather";
import SectionCard from "../../../components/common/SectionCard";
import StatusBadge from "../../../components/common/StatusBadge";
import CustomerPicker from "../../sales/components/CustomerPicker";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import CauseChip from "../components/CauseChip";
import AlarmSheetPrintButton from "../components/AlarmSheetPrintButton";
import { PRINT_BASE, PRINT_TD, PRINT_TH, PrintHeader, printElement } from "../../../components/common/printSheet";
import { useGetAlarmLookupsQuery, useGetAlarmResponsesQuery, useGetMobileUnitsQuery } from "../alarmsApi";
import {
  ALARM_STATUSES, ALARM_STATUS_CLASS, ALARM_STATUS_LABEL, ARRIVAL_WARN_METERS, CAUSE_LABEL, CAUSE_OPTIONS, CAUSE_STYLE,
  INCIDENT_LABEL,
  formatAccuracy, formatDateTime, formatDistance, formatDuration, formatTime, isLowAccuracy,
} from "../alarmFormat";
import { paths } from "../../../routes/paths";

const portal = typeof document !== "undefined" ? document.body : undefined;

const average = (values) => {
  const v = values.filter((x) => x != null);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};

const summarize = (rows) => ({
  total: rows.length,
  byCause: Object.fromEntries(CAUSE_OPTIONS.map((c) => [c.code, rows.filter((r) => r.alarmCause === c.code).length])),
  unclassified: rows.filter((r) => !r.alarmCause && ["REPORTED", "CLOSED", "CONVERTED"].includes(r.status)).length,
  pushed: rows.filter((r) => r.status === "CONVERTED").length,
  cancelled: rows.filter((r) => r.status === "CANCELLED").length,
  avgResponse: average(rows.map((r) => r.responseSeconds)),
  avgTravel: average(rows.map((r) => r.travelSeconds)),
  avgTotal: average(rows.map((r) => r.totalSeconds)),
});

// Far from the pin only counts as a red flag when the position was a real GPS fix.
const arrivalFlagged = (r) =>
  !isLowAccuracy(r.arrivedAccuracyMeters) && r.arrivalToCustomerMeters != null && r.arrivalToCustomerMeters > ARRIVAL_WARN_METERS;

const outcomeText = (r) =>
  r.status === "CONVERTED" ? `Tech ${r.technicalWorkNumber || ""}` : ALARM_STATUS_LABEL[r.status] || r.status;

// Printable list - A4 landscape, one line per alarm, every time on the line.
const ReportPrint = ({ rows, summary, filterText, branchCode }) => (
  <div style={{ ...PRINT_BASE, padding: "6px" }}>
    <PrintHeader
      title="Rapid Response Report"
      subtitle={filterText}
      lines={[
        [`Alarms ${summary.total}`,
          ...CAUSE_OPTIONS.map((c) => `${c.label} ${summary.byCause[c.code]}`),
          `Pushed to Tech ${summary.pushed}`, `Cancelled ${summary.cancelled}`].join(" · "),
        `Average response ${formatDuration(summary.avgResponse)} · travel ${formatDuration(summary.avgTravel)} · total ${formatDuration(summary.avgTotal)}`,
      ]}
      branchCode={branchCode}
    />
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {["No.", "Customer / site", "Incident", "Unit / attended by", "Signal", "Dispatched", "Opened", "Arrived", "Reported",
            "Resp.", "Travel", "Total", "Start→site", "Arrival off", "Cause", "Outcome / action taken"].map((h) => (
            <th key={h} style={PRINT_TH}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.recordGuid} style={{ background: r.alarmCause === "THEFT_ATTEMPT" ? "#FBE9E7" : undefined }}>
            <td style={PRINT_TD}>{r.responseNumber}</td>
            <td style={PRINT_TD}><b>{r.customerName}</b>{r.siteLabel ? <div>{r.siteLabel}</div> : null}</td>
            <td style={PRINT_TD}>{INCIDENT_LABEL[r.incidentType] || r.incidentType}<div>{r.incidentDescription}</div></td>
            <td style={PRINT_TD}>{r.unitName}{r.crewNames ? <div>{r.crewNames}</div> : null}</td>
            <td style={PRINT_TD}>{dayjs(r.signalReceivedAt).format("DD/MM HH:mm")}</td>
            <td style={PRINT_TD}>{formatTime(r.dispatchedDate)}</td>
            <td style={PRINT_TD}>{r.openedDate ? formatTime(r.openedDate) : "—"}</td>
            <td style={PRINT_TD}>{r.arrivedDate ? `${formatTime(r.arrivedDate)}${r.arrivedQrVerified ? " QR" : ""}` : "—"}</td>
            <td style={PRINT_TD}>{r.reportedDate ? formatTime(r.reportedDate) : "—"}</td>
            <td style={PRINT_TD}>{formatDuration(r.responseSeconds)}</td>
            <td style={PRINT_TD}>{formatDuration(r.travelSeconds)}</td>
            <td style={PRINT_TD}>{formatDuration(r.totalSeconds)}</td>
            <td style={PRINT_TD}>{formatDistance(r.startToCustomerMeters)}</td>
            <td style={{ ...PRINT_TD, color: arrivalFlagged(r) ? "#C0392B" : undefined }}>
              {formatDistance(r.arrivalToCustomerMeters)}
              {isLowAccuracy(r.arrivedAccuracyMeters) ? <div>LOW GPS {formatAccuracy(r.arrivedAccuracyMeters)}</div> : null}
            </td>
            <td style={PRINT_TD}>
              {r.alarmCause ? CAUSE_LABEL[r.alarmCause] || r.alarmCause : ""}
              {r.causeRemarks ? <div>{r.causeRemarks}</div> : null}
            </td>
            <td style={PRINT_TD}>
              {outcomeText(r)}
              {r.actionTaken ? <div>{r.actionTaken}</div> : null}
              {r.cancelReason ? <div>{r.cancelReason}</div> : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AlarmResponseReportPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const today = dayjs().format("YYYY-MM-DD");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [unitGuid, setUnitGuid] = useState("");
  const [customer, setCustomer] = useState(null);
  const [status, setStatus] = useState("");
  const [incidentType, setIncidentType] = useState("");
  const [alarmCause, setAlarmCause] = useState("");
  const [search, setSearch] = useState("");

  const { data: lookups } = useGetAlarmLookupsQuery();
  const branchId = customer?.customer.branchId ?? selectedBranchId ?? undefined;
  const { data: units = [] } = useGetMobileUnitsQuery({ ...(branchId != null ? { branchId } : {}), includeInactive: true });

  const params = {
    ...(branchId != null ? { branchId } : {}),
    dateFrom,
    dateTo,
    ...(unitGuid ? { unitRecordGuid: unitGuid } : {}),
    ...(customer ? { custId: customer.customer.custId } : {}),
    ...(status ? { status } : {}),
    ...(incidentType ? { incidentType } : {}),
    ...(alarmCause ? { alarmCause } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  };
  const { data: rows = [], isFetching, error } = useGetAlarmResponsesQuery(params, { skip: !dateFrom || !dateTo });
  const summary = useMemo(() => summarize(rows), [rows]);

  const branchCode = availableBranches.find((b) => b.branchId === branchId)?.branchCode || rows[0]?.branchCode;
  const unitName = units.find((u) => u.recordGuid === unitGuid)?.unitName;
  const filterText = [
    dateFrom === dateTo ? dayjs(dateFrom).format("DD MMM YYYY") : `${dayjs(dateFrom).format("DD MMM YYYY")} - ${dayjs(dateTo).format("DD MMM YYYY")}`,
    branchCode ? `Branch ${branchCode}` : "All branches",
    unitName && `Unit ${unitName}`,
    customer && `Customer ${customer.customer.custName}`,
    status && ALARM_STATUS_LABEL[status],
    incidentType && INCIDENT_LABEL[incidentType],
    alarmCause && (alarmCause === "NONE" ? "Cause not set" : CAUSE_LABEL[alarmCause]),
    search.trim() && `"${search.trim()}"`,
  ].filter(Boolean).join(" · ");

  const print = () => {
    if (!printElement(<ReportPrint rows={rows} summary={summary} filterText={filterText} branchCode={branchCode} />,
      { title: `Rapid Response Report ${dateFrom}`, page: "A4 landscape" }))
      toast.error("Allow pop-ups for this site to print.");
  };

  const incidentTypes = lookups?.incidentTypes || Object.entries(INCIDENT_LABEL).map(([code, label]) => ({ code, label }));
  const causes = CAUSE_OPTIONS;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Rapid Response Report</h4>
              <h6>Reported, arrival and report times, locations, who attended and what was done</h6>
            </div>
          </div>
          <div className="page-btn">
            <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-1" onClick={print} disabled={!rows.length}>
              <Printer size={15} /> Print
            </button>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-6 col-md-2">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label small mb-1">Customer</label>
                <CustomerPicker branchId={selectedBranchId} value={customer} onChange={setCustomer} menuPortalTarget={portal} isClearable />
              </div>
              <div className="col-md-4">
                <label className="form-label small mb-1">Search</label>
                <input className="form-control form-control-sm" value={search} placeholder="Number, customer, unit, description"
                  onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small mb-1">Unit</label>
                <select className="form-select form-select-sm" value={unitGuid} onChange={(e) => setUnitGuid(e.target.value)}>
                  <option value="">All units</option>
                  {units.map((u) => <option key={u.recordGuid} value={u.recordGuid}>{u.unitName}{u.isActive ? "" : " (inactive)"}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small mb-1">Status</label>
                <select className="form-select form-select-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">All</option>
                  {ALARM_STATUSES.map((s) => <option key={s} value={s}>{ALARM_STATUS_LABEL[s]}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small mb-1">Incident</label>
                <select className="form-select form-select-sm" value={incidentType} onChange={(e) => setIncidentType(e.target.value)}>
                  <option value="">All</option>
                  {incidentTypes.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small mb-1">Cause</label>
                <select className="form-select form-select-sm" value={alarmCause} onChange={(e) => setAlarmCause(e.target.value)}>
                  <option value="">All</option>
                  {causes.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  <option value="NONE">Not set</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span className="badge badge-linesecondary" style={{ fontSize: 13 }}>Alarms {summary.total}</span>
          {/* One clickable count per cause - clicking filters to it, clicking again clears. */}
          {CAUSE_OPTIONS.map((c) => (
            <button key={c.code} type="button" className="badge border-0"
              style={{ fontSize: 13, ...CAUSE_STYLE[c.code], outline: alarmCause === c.code ? "2px solid #000" : undefined }}
              onClick={() => setAlarmCause(alarmCause === c.code ? "" : c.code)}>
              {c.label} {summary.byCause[c.code]}
            </button>
          ))}
          <span className="badge badge-linewarning" style={{ fontSize: 13 }}>Cause not set {summary.unclassified}</span>
          <span className="badge badge-linesuccess" style={{ fontSize: 13 }}>Pushed to Tech {summary.pushed}</span>
          <span className="badge badge-lineinfo" style={{ fontSize: 13 }}>
            Avg response {formatDuration(summary.avgResponse)} · travel {formatDuration(summary.avgTravel)} · total {formatDuration(summary.avgTotal)}
          </span>
        </div>

        <SectionCard icon={FileText} title={isFetching ? "Loading..." : `${rows.length} alarm${rows.length === 1 ? "" : "s"}`}>
          {error ? (
            <div className="alert alert-danger mb-0">{error.message || "Could not load the report."}</div>
          ) : rows.length === 0 ? (
            <p className="text-muted small mb-0">No alarms for these filters.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th aria-label="Print" /><th>Customer</th><th>Incident</th><th>Unit / attended by</th><th>Signal</th><th>Dispatched</th>
                    <th>Opened</th><th>Arrived</th><th>Reported</th><th>Response</th><th>Travel</th><th>Total</th>
                    <th>Start→site</th><th>Arrival off pin</th><th>Cause</th><th>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.recordGuid}>
                      {/* First column so it stays in view when the wide table scrolls. */}
                      <td>
                        <AlarmSheetPrintButton recordGuid={r.recordGuid} compact />
                      </td>
                      <td>
                        <Link to={paths.alarmResponseDetail(r.recordGuid)} className="fw-bold text-reset">{r.customerName}</Link>
                        <div className="text-muted small">{r.responseNumber}{r.siteLabel ? ` · ${r.siteLabel}` : ""}</div>
                      </td>
                      <td>
                        {INCIDENT_LABEL[r.incidentType] || r.incidentType}
                        <div className="text-muted small text-truncate" style={{ maxWidth: 180 }} title={r.incidentDescription}>{r.incidentDescription}</div>
                      </td>
                      <td>
                        {r.unitName}
                        {r.crewNames && <div className="text-muted small">{r.crewNames}</div>}
                      </td>
                      <td className="text-nowrap">{formatDateTime(r.signalReceivedAt)}</td>
                      <td className="text-nowrap">{formatTime(r.dispatchedDate)}</td>
                      <td className="text-nowrap">{r.openedDate ? formatTime(r.openedDate) : "—"}</td>
                      <td className="text-nowrap">
                        {r.arrivedDate ? formatTime(r.arrivedDate) : "—"}
                        {r.arrivedQrVerified && <span className="badge badge-linesuccess ms-1">QR</span>}
                      </td>
                      <td className="text-nowrap">{r.reportedDate ? formatTime(r.reportedDate) : "—"}</td>
                      <td>{formatDuration(r.responseSeconds)}</td>
                      <td>{formatDuration(r.travelSeconds)}</td>
                      <td>{formatDuration(r.totalSeconds)}</td>
                      <td>{formatDistance(r.startToCustomerMeters)}</td>
                      <td className={arrivalFlagged(r) ? "text-danger fw-bold" : ""}>
                        {formatDistance(r.arrivalToCustomerMeters)}
                        {isLowAccuracy(r.arrivedAccuracyMeters) && (
                          <span
                            className="badge badge-linewarning ms-1"
                            title={`Arrival position only accurate to ${formatAccuracy(r.arrivedAccuracyMeters)} - not a GPS fix`}
                          >
                            Low GPS
                          </span>
                        )}
                      </td>
                      <td><CauseChip cause={r.alarmCause} /></td>
                      <td>
                        <StatusBadge status={r.status} classMap={ALARM_STATUS_CLASS} labelMap={ALARM_STATUS_LABEL} />
                        {r.technicalWorkRecordGuid && (
                          <Link to={paths.technicalWorkDetail(r.technicalWorkRecordGuid)} className="small ms-1">{r.technicalWorkNumber}</Link>
                        )}
                        {r.actionTaken && <div className="text-muted small text-truncate" style={{ maxWidth: 200 }} title={r.actionTaken}>{r.actionTaken}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default AlarmResponseReportPage;
